/**
 * Product V2 Model
 *
 * TypeScript interfaces matching the backend ProductV2 entity.
 * Used for API calls, form state, and component props.
 *
 * @see /packages/pms-api-v2/src/pms/pms-product-v2/entities/product-v2.entity.ts
 */

import type { ProductPrice, ProductPack, ProductSample, ProductPricing } from '../utils/product-pricing';

export type {
  ProductPrice,
  ProductPack,
  ProductSample,
  ProductPricing,
  ResolvedPrice,
  ResolvedPack,
  ResolvedSample,
  PriceUnit,
  PriceSource,
} from '../utils/product-pricing';

// =============================================================================
// Axis Types
// =============================================================================

/**
 * Color axis value with optional hex code
 */
export interface ColorAxis {
  /** Display value for the color (e.g., "Oak Natural", "Marron clair") */
  value: string;
  /** Optional hex color code (e.g., "#C4A76B") */
  hex?: string;
}

/**
 * Size/dimension axis value
 * Supports 2D (width × height) and 3D (width × height × depth)
 */
export interface SizeAxis {
  /** Width in specified unit */
  width: number;
  /** Height/Length in specified unit */
  height: number;
  /** Depth for 3D products (furniture, etc.) */
  depth?: number;
  /** Unit of measurement (default: "cm") */
  unit: string;
}

/**
 * Product axes - variant differentiation values
 * Standard axes (color, size, pattern) plus any custom axes
 */
export interface ProductAxes {
  /** Color axis with display value and optional hex */
  color?: ColorAxis;
  /** Size/dimensions axis */
  size?: SizeAxis;
  /** Pattern type for surface products (e.g., "herringbone", "stackbond") */
  pattern?: string;
  /** Custom axes - any additional axes defined by family */
  [key: string]: ColorAxis | SizeAxis | string | number | null | undefined;
}

// =============================================================================
// Visualization Types
// =============================================================================

/**
 * Grout settings for surface products (tiles, etc.)
 */
export interface GroutSettings {
  /** Grout line color (default: "#00000000" - transparent) */
  color: string;
  /** Grout line width in pixels (default: 0) */
  width: number;
}

/**
 * Parameters for seamless texture generation
 */
export interface SeamlessParams {
  /** Pattern type (e.g., "stackbond", "herringbone", "bricks") */
  pattern?: string;
  /** Texture width in meters */
  textureWidth?: number;
  /** Texture height in meters */
  textureHeight?: number;
  /** Individual plank width in cm */
  plankWidth?: number;
  /** Individual plank height in cm */
  plankHeight?: number;
  /** Crop position for texture preview */
  crop?: { x: number; y: number };
  /** Zoom level for texture preview */
  zoom?: number;
  /** Layout options (CSV layout "stackbond:mirror", "bricks:third", "hexagonal:flip"; see parseLayout). */
  mirror?: boolean;
  randomize?: boolean;
  offset?: 'random' | 'half' | 'third';
  flip?: boolean;
}

/**
 * How a surface product reflects light, in py-visualizer's names (pms-api-v2
 * surface-finish.ts). Absent means matte. Shoppers may turn reflections on for
 * the others, where Ardealy has finishes on for the vendor.
 */
export const SURFACE_FINISHES = ['matte', 'satin', 'glossy', 'lacquered', 'handmade'] as const;
export type SurfaceFinish = typeof SURFACE_FINISHES[number];

/**
 * Surface product visualization (tiles, hardwood, laminate)
 */
export interface SurfaceVisualization {
  /** URL to seamless material texture */
  materialPath?: string;
  /** URLs to individual plank images */
  materialPaths?: string[];
  /** Group ID for plank variations */
  plankGroupId?: string;
  /** Grout line settings */
  grout?: GroutSettings;
  /** Seamless generation parameters */
  seamlessParams?: SeamlessParams;
  /** Whether this variant uses multiple plank images (true) or a single seamless texture (false) */
  isMultiple?: boolean;
  /** Whether shoppers may change the grout in the visualizer (grout v2, with the vendor's groutEnabled) */
  groutEditable?: boolean;
  /** How it reflects light; absent means matte */
  finish?: SurfaceFinish;
  /** Grout layers made by the seamless generator (read-only; the API manages them) */
  groutLayers?: {
    key: string;
    status: 'pending' | 'ready' | 'unsupported' | 'failed';
    basePath?: string;
    jointPath?: string;
    textureWidth?: number;
    textureHeight?: number;
  };
}

/**
 * 3D model product visualization (furniture, fixtures).
 *
 * Shared by all three SDK features: Visualizer (room placement), 3D modal viewer,
 * and AR (native Scene Viewer on Android via GLTF, AR Quick Look on iOS via USDZ).
 */
export interface Model3DVisualization {
  /** URL to 3D model file (GLTF/GLB). Used by Android AR Scene Viewer + 3D modal + room visualizer. */
  modelPath?: string;
  /** URL to texture file for the 3D model */
  texturePath?: string;
  /** URL to preview image of the 3D model */
  previewImagePath?: string;
  /** URL to original product image (full resolution) */
  originalImagePath?: string;
  /** URL to iOS AR Quick Look asset (USDZ). Required for AR on iPhones/iPads. Optional — AR on iOS degrades gracefully when missing. */
  usdzPath?: string;
  /** Per-product hint for the 3D modal — whether the camera should auto-rotate when idle. */
  autoRotate?: boolean;
  /** Placement mode forwarded to Scene Viewer (Android) and AR Quick Look (iOS). */
  arPlacement?: 'floor' | 'wall';
}

/**
 * AR model visualization.
 *
 * @deprecated Populated only by the V1→V2 migration service for legacy parity; no runtime read path
 * consumes this. New code reads/writes {@link Model3DVisualization.modelPath} + `usdzPath` instead.
 */
export interface ArVisualization {
  /** URL to AR model file (GLB/GLTF) */
  model?: string;
  /** URL to texture file for the AR model */
  modelTexture?: string;
}

/**
 * Visualization type discriminator
 */
export type VisualizationType = 'surface' | 'model3d' | 'image_only';

/**
 * Polymorphic visualization configuration
 * Type determines which sub-object is populated
 */
export interface ProductVisualization {
  /** Type of visualization - determines which renderer to use */
  type: VisualizationType;
  /** URL to product thumbnail image */
  thumbnailPath?: string;
  /** Surface product visualization data (tiles, hardwood) */
  surface?: SurfaceVisualization;
  /** 3D product visualization data (furniture) */
  model3d?: Model3DVisualization;
  /** Auto-extracted dominant colors from product image */
  extractedColors?: string[];
  /** AR model visualization data */
  ar?: ArVisualization;
}

// =============================================================================
// Organization Types
// =============================================================================

/**
 * Product organization and categorization
 */
export interface ProductOrganization {
  /** Product type classification (e.g., "Hardwood", "Tile", "Dining Chair") */
  productType?: string;
  /** Application types (e.g., ["floor", "wall"]) */
  applicationTypes?: string[];
}

// =============================================================================
// Commerce Types
// =============================================================================

/**
 * Commerce: shopping links, and what the variant sells for (Room to Revenue A1).
 *
 * `price`, `pack` and `sample` are per variant. `null` clears a stored value;
 * an update that leaves a key out keeps what is stored.
 */
export interface ProductCommerce {
  /** Product page URL */
  link?: string;
  /** Direct add-to-cart URL */
  addToCartLink?: string;
  /** Selling price and what it is per; `source` says who stated it. */
  price?: ProductPrice | null;
  /** What one box covers / holds. */
  pack?: ProductPack | null;
  /** Whether a sample can be ordered (price 0 = free). */
  sample?: ProductSample | null;
}

// =============================================================================
// Main Product V2 Interface
// =============================================================================

/**
 * Product V2 - Full product document interface
 *
 * Represents a single product variant in the V2 data model.
 * Products are grouped by `id` (product group) and identified by `uuid` (unique variant).
 */
export interface ProductV2 {
  // ───────────────────────────────────────────────────────────────
  // Core Identity
  // ───────────────────────────────────────────────────────────────

  /** MongoDB ObjectId (string representation) */
  _id?: string;
  /** Product group ID (shared by all variants of the same product) */
  id: string;
  /** Unique variant UUID */
  uuid: string;
  /** Vendor ObjectId reference (string representation) */
  vendor: string;

  // ───────────────────────────────────────────────────────────────
  // Family Reference
  // ───────────────────────────────────────────────────────────────

  /** Product family UUID */
  familyId?: string;
  /** Product family slug for quick filtering (e.g., "surface-products") */
  familySlug?: string;
  /** Furniture category slug (synced with YOLO detection taxonomy), e.g. "rugs" */
  category?: string;

  // ───────────────────────────────────────────────────────────────
  // Basic Info
  // ───────────────────────────────────────────────────────────────

  /** Product name */
  name: string;
  /** Brand name */
  brand?: string;
  /** Product description */
  description?: string;
  /** Stock Keeping Unit */
  sku?: string;

  // ───────────────────────────────────────────────────────────────
  // Variant Axes
  // ───────────────────────────────────────────────────────────────

  /** Variant axes (color, size, pattern, custom) */
  axes: ProductAxes;

  // ───────────────────────────────────────────────────────────────
  // Visualization
  // ───────────────────────────────────────────────────────────────

  /** Visualization configuration */
  visualization: ProductVisualization;

  // ───────────────────────────────────────────────────────────────
  // Gallery Images
  // ───────────────────────────────────────────────────────────────

  /**
   * Contextual / lifestyle product image URLs (e.g. usage shots, packaging).
   * Populated by vendor upload or API integration sync. Never touched by the
   * visualization pipeline — `visualization.thumbnailPath` is app-generated only.
   */
  galleryImages?: string[];

  // ───────────────────────────────────────────────────────────────
  // Family-Specific Attributes
  // ───────────────────────────────────────────────────────────────

  /** Dynamic family-specific attributes */
  attributes: Record<string, unknown>;

  /** @deprecated Legacy specifics field - use attributes instead */
  specifics?: Record<string, unknown>;

  // ───────────────────────────────────────────────────────────────
  // Organization
  // ───────────────────────────────────────────────────────────────

  /** Product organization and categorization */
  organization: ProductOrganization;

  // ───────────────────────────────────────────────────────────────
  // Commerce
  // ───────────────────────────────────────────────────────────────

  /** Commerce links */
  commerce: ProductCommerce;

  /**
   * What shoppers see for price, packaging and samples, resolved by the API
   * from the stored fields, the vendor's shop and their product page.
   * READ-ONLY: computed on every read and never sent back in a payload.
   */
  pricing?: ProductPricing;

  // ───────────────────────────────────────────────────────────────
  // Variant State
  // ───────────────────────────────────────────────────────────────

  /** Is this the default variant for display */
  isDefault: boolean;
  /** Is this variant visible in visualizer */
  isVisible: boolean;
  /** Is this product active (not soft-deleted) */
  active: boolean;
  /**
   * Marks the product as an incomplete draft (e.g. created by an API
   * integration sync). Drafts are hidden from the visualizer and surfaced
   * in the catalog with a "draft" badge so the vendor can complete missing
   * fields (family, axes, visualization) and publish.
   */
  isDraft?: boolean;
  /** Display order among sibling variants (lower = first) */
  siblingOrder?: number;

  // ───────────────────────────────────────────────────────────────
  // Grouping
  // ───────────────────────────────────────────────────────────────

  /** UUIDs of sibling variants in the same product group */
  siblings: string[];

  // ───────────────────────────────────────────────────────────────
  // Migration Tracking
  // ───────────────────────────────────────────────────────────────

  /** Original V1 product ID (for migration tracking) */
  _v1Id?: string;
  /** Migration timestamp */
  _migratedAt?: Date;

  // ───────────────────────────────────────────────────────────────
  // Timestamps
  // ───────────────────────────────────────────────────────────────

  /** Creation timestamp */
  createdAt?: Date;
  /** Last update timestamp */
  updatedAt?: Date;
}

// =============================================================================
// API Payload Types
// =============================================================================

/**
 * Payload for creating a new product
 */
export type CreateProductV2Payload = Omit<
  ProductV2,
  '_id' | 'createdAt' | 'updatedAt' | '_v1Id' | '_migratedAt' | 'pricing'
>;

/**
 * Payload for updating an existing product
 */
export type UpdateProductV2Payload = Partial<
  Omit<ProductV2, '_id' | 'uuid' | 'vendor' | 'createdAt' | 'updatedAt' | '_v1Id' | '_migratedAt' | 'pricing'>
>;

/**
 * Bulk operation types
 */
export type BulkOperationType = 'create' | 'update' | 'delete';

/**
 * Single bulk operation item
 */
export interface BulkOperationItem {
  operation: BulkOperationType;
  uuid?: string; // Required for update/delete
  data?: CreateProductV2Payload | UpdateProductV2Payload;
  generateSeamless?: boolean;
  seamlessParams?: {
    pattern: string;
    textureWidth?: number;
    textureHeight?: number;
    groutLineColor: string;
    groutLineWidth: number;
    plankWidth: number;
    plankHeight: number;
    /** Layout options (CSV layout "stackbond:mirror", "bricks:third", "hexagonal:flip"; see parseLayout). */
    mirror?: boolean;
    randomize?: boolean;
    offset?: 'random' | 'half' | 'third';
    flip?: boolean;
  };
}

/**
 * Bulk operations request
 */
export interface BulkOperationsRequest {
  operations: BulkOperationItem[];
}

/**
 * Bulk operations response
 */
export interface BulkOperationsResponse {
  batchId: string;
  message: string;
  totalJobs: number;
}

/**
 * Paginated product list response
 */
export interface ProductV2ListResponse {
  data: ProductV2[];
  limit: number;
  offset: number;
  nextPage: number;
  total: number;
}

// =============================================================================
// Utility Types
// =============================================================================

/**
 * Axis value - unified type for all axis values
 */
export type AxisValue = string | number | ColorAxis | SizeAxis | null;

/**
 * Extract the raw value from an axis
 */
export type AxisRawValue<T extends AxisValue> = T extends ColorAxis
  ? string
  : T extends SizeAxis
    ? { width: number; height: number; depth?: number; unit: string }
    : T;

/**
 * Product without MongoDB-specific fields
 */
export type ProductV2WithoutMongo = Omit<ProductV2, '_id' | 'createdAt' | 'updatedAt'>;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create a default empty ProductV2 structure
 */
export function createEmptyProductV2(
  vendor: string,
  familyId?: string,
  familySlug?: string
): Omit<ProductV2, '_id' | 'createdAt' | 'updatedAt'> {
  return {
    id: '', // Will be generated
    uuid: '', // Will be generated
    vendor,
    familyId,
    familySlug,
    name: '',
    brand: '',
    description: '',
    sku: '',
    axes: {},
    visualization: {
      type: 'surface',
      thumbnailPath: '',
      surface: {
        materialPath: '',
        materialPaths: [],
        grout: { color: '#00000000', width: 0 },
        seamlessParams: {},
      },
    },
    galleryImages: [],
    attributes: {},
    organization: {
      productType: '',
      applicationTypes: [],
    },
    commerce: {
      link: '',
      addToCartLink: '',
    },
    isDefault: false,
    isVisible: true,
    active: true,
    isDraft: false,
    siblings: [],
  };
}

/**
 * Create default visualization based on type
 */
export function createDefaultVisualization(type: VisualizationType): ProductVisualization {
  const base: ProductVisualization = {
    type,
    thumbnailPath: '',
  };

  switch (type) {
    case 'surface':
      return {
        ...base,
        surface: {
          materialPath: '',
          materialPaths: [],
          grout: { color: '#00000000', width: 0 },
          seamlessParams: {},
        },
      };
    case 'model3d':
      return {
        ...base,
        model3d: {
          modelPath: '',
          originalImagePath: '',
        },
      };
    case 'image_only':
    default:
      return base;
  }
}

/**
 * Check if a product has surface visualization
 */
export function isSurfaceProduct(product: ProductV2): boolean {
  return product.visualization.type === 'surface';
}

/**
 * Check if a product has 3D model visualization
 */
export function is3DProduct(product: ProductV2): boolean {
  return product.visualization.type === 'model3d';
}

/**
 * Check if a product has image-only visualization
 */
export function isImageOnlyProduct(product: ProductV2): boolean {
  return product.visualization.type === 'image_only';
}

/**
 * Get display string for a size axis
 */
export function formatSizeAxis(size: SizeAxis): string {
  if (size.depth != null) {
    return `${size.width} × ${size.height} × ${size.depth} ${size.unit}`;
  }
  return `${size.width} × ${size.height} ${size.unit}`;
}

/**
 * Get display string for a color axis
 */
export function formatColorAxis(color: ColorAxis): string {
  return color.value;
}

/**
 * Get display string for any axis value
 */
export function formatAxisValue(value: AxisValue): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if ('width' in value && 'height' in value) {
    return formatSizeAxis(value as SizeAxis);
  }
  if ('value' in value) {
    return formatColorAxis(value as ColorAxis);
  }
  return String(value);
}

/**
 * Check if two axis values are equal
 */
export function areAxisValuesEqual(a: AxisValue, b: AxisValue): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === 'object' && typeof b === 'object') {
    // Compare SizeAxis
    if ('width' in a && 'width' in b) {
      const sizeA = a as SizeAxis;
      const sizeB = b as SizeAxis;
      return (
        sizeA.width === sizeB.width &&
        sizeA.height === sizeB.height &&
        sizeA.depth === sizeB.depth &&
        sizeA.unit === sizeB.unit
      );
    }
    // Compare ColorAxis
    if ('value' in a && 'value' in b) {
      const colorA = a as ColorAxis;
      const colorB = b as ColorAxis;
      return colorA.value === colorB.value && colorA.hex === colorB.hex;
    }
  }

  return a === b;
}

/**
 * Generate a unique key for a variant based on its axes
 */
export function generateVariantKey(axes: ProductAxes): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(axes)) {
    if (value != null) {
      parts.push(`${key}:${formatAxisValue(value as AxisValue)}`);
    }
  }

  return parts.sort().join('|');
}

/**
 * Clone a product for editing (removes server-managed fields)
 */
export function cloneProductForEdit(product: ProductV2): ProductV2WithoutMongo {
  const { _id, createdAt, updatedAt, ...rest } = product;
  return JSON.parse(JSON.stringify(rest));
}
